import {
  IFieldDefinition,
  LayoutElement,
  isBooleanFieldElement,
  isChildSelectConfig,
  isColumnElement,
  isFieldElement,
  isImageElement,
  isInputFieldElement,
  isLayoutTableFieldElement,
  isOptionsFieldElement,
  isTexElement,
} from "@entaconsulting/form";
import { isArray, isEmpty, isObject, isUndefined } from "lodash";
import { useMemo, useState } from "react";
import { FIELDS_BY_SECTION, PanelSections } from "../constants";
import useDynamicField from "./useDynamicField";

const useIsExpanded = (sectionName: PanelSections) => {
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);

  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");
  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const isImageEl = isImageElement(element);
  const isTextEl = isTexElement(element);
  const isColumnEl = isColumnElement(element);
  const isFieldEl = isFieldElement(element);
  const isInputFieldEl = isFieldEl && isInputFieldElement(element, fieldDef);
  const isBooleanFieldEl =
    isFieldEl && isBooleanFieldElement(element, fieldDef);
  const isOptionsFieldEl =
    isFieldEl && isOptionsFieldElement(element, fieldDef);
  const isTableFieldEl = isLayoutTableFieldElement(element);
  const isAttachmentFieldEl = fieldDef.type === "attachment";

  const handleChangeIsExpanded = (event: any, expanded: boolean) => {
    setIsExpanded(expanded);
  };

  const isDefaultExpanded = useMemo(() => {
    const allPropertiesElement: any = { ...element, ...fieldDef };

    switch (sectionName) {
      case PanelSections.General:
        return true;
      case PanelSections.Appearance:
      case PanelSections.Behavior:
      case PanelSections.Validation:
        return FIELDS_BY_SECTION[sectionName]?.some(
          (field) =>
            !isEmpty(allPropertiesElement[field]) ||
            !!allPropertiesElement[field]
        );
      case PanelSections.Options:
        const hasOptions =
          (isArray(fieldDef.options) && fieldDef.options.length > 0) ||
          (isObject(fieldDef.options) &&
            isChildSelectConfig(fieldDef.options) &&
            Object.keys(fieldDef.options.childOptions).length > 0);

        return isFieldEl && isOptionsFieldEl && hasOptions;
      case PanelSections.TableColumns:
        return isTableFieldEl && element.listFields.fields.length > 0;
      case PanelSections.ValidationExpressions:
        return isFieldEl && !isEmpty(fieldDef.validationExpressions);
      default:
        return false;
    }
  }, [
    element,
    fieldDef,
    isFieldEl,
    isOptionsFieldEl,
    sectionName,
    isTableFieldEl,
  ]);

  const hide = useMemo(() => {
    switch (sectionName) {
      case PanelSections.NameAndType:
        return !isFieldEl;
      case PanelSections.Appearance:
        return (
          (!isColumnEl &&
            !isImageEl &&
            !isFieldEl &&
            !isTextEl &&
            !isTableFieldEl) ||
          (isFieldEl &&
            !isOptionsFieldEl &&
            !isBooleanFieldEl &&
            !isInputFieldEl &&
            !isTableFieldEl)
        );
      case PanelSections.Validation:
        return !isInputFieldEl && !isAttachmentFieldEl;
      case PanelSections.Options:
        return !isOptionsFieldEl;
      case PanelSections.TableColumns:
        return !isTableFieldEl;
      case PanelSections.ValidationExpressions:
        return !isFieldEl;
      default:
        return false;
    }
  }, [
    isAttachmentFieldEl,
    isBooleanFieldEl,
    isColumnEl,
    isFieldEl,
    isImageEl,
    isInputFieldEl,
    isOptionsFieldEl,
    isTableFieldEl,
    isTextEl,
    sectionName,
  ]);

  return {
    isExpanded,
    handleChangeIsExpanded: isUndefined(isExpanded)
      ? undefined
      : handleChangeIsExpanded,
    isDefaultExpanded,
    hide,
  };
};

export default useIsExpanded;
